import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { RiDatabaseLine, RiKeyLine, RiCloseLine } from '@remixicon/react';

interface TableNodeData {
  label: string;
  columns: Array<{
    name: string;
    type: string;
    isPrimaryKey?: boolean;
    isForeignKey?: boolean;
  }>;
  onColumnSelect: (columnName: string) => void;
  onRemove?: () => void;
  selectedColumns: string[];
}

export const TableNode = memo(({ data }: NodeProps<TableNodeData>) => {
  return (
    <Card className="min-w-[280px] shadow-lg border-2 hover:shadow-xl transition-shadow">
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-primary border-2 border-background"
      />

      <CardHeader className="p-3 bg-gradient-to-br from-primary/10 to-primary/5 border-b">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded bg-primary/20">
            <RiDatabaseLine className="w-3.5 h-3.5 text-primary" />
          </div>
          <h3 className="font-semibold text-sm flex-1">{data.label}</h3>
          {data.onRemove && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
              onClick={data.onRemove}
            >
              <RiCloseLine className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="max-h-[320px] overflow-y-auto">
          {data.columns.length === 0 ? (
            <div className="p-3 text-center text-xs text-muted-foreground">
              No columns
            </div>
          ) : (
            data.columns.map((column) => (
              <div
                key={column.name}
                className="flex items-center gap-2 p-2.5 hover:bg-accent/80 border-b last:border-b-0 transition-colors group"
              >
                <Checkbox
                  checked={data.selectedColumns.includes(column.name)}
                  onCheckedChange={() => data.onColumnSelect(column.name)}
                  className="data-[state=checked]:bg-primary"
                />
                <div className="flex-1 flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-mono truncate">{column.name}</span>
                  {column.isPrimaryKey && (
                    <RiKeyLine className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" title="Primary Key" />
                  )}
                </div>
                <span className="text-xs text-muted-foreground/80 font-mono">{column.type}</span>
              </div>
            ))
          )}
        </div>
      </CardContent>

      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-primary border-2 border-background"
      />
    </Card>
  );
});

TableNode.displayName = 'TableNode';
